import React from 'react';
import { useTranslation } from '@shared/core';
import { EmphasisedValue } from '@shared/components/EmphasisedValue';
import { phraseSlot, withPhraseValues } from '@shared/utils/i18n/phraseValues';

const BALANCE_SLOT = phraseSlot('BALANCE');
const SAVINGS_SLOT = phraseSlot('SAVINGS');
const PERCENT_SLOT = phraseSlot('PERCENT');

type FundBalanceSavingsCopyArgs = Readonly<{
  fundBalanceDisplay?: string;
  fundCouponSavingsDisplay?: string;
  fundCouponPercentOff?: number;
  /** `'it'` when a title above already states the balance, so the sentence does not repeat it. Defaults to naming the balance. */
  subject?: 'balance' | 'it';
}>;

/**
 * The "Use your {balance} … Save up to an extra {savings}" sentence, shared so every fund surface quotes a couple the same saving.
 * Undefined without a balance, so each caller keeps its own fallback.
 */
export const useFundBalanceSavingsCopy = ({ fundBalanceDisplay, fundCouponSavingsDisplay, fundCouponPercentOff, subject = 'balance' }: FundBalanceSavingsCopyArgs) => {
  const { tq } = useTranslation('yourRegistry');

  if (!fundBalanceDisplay) return undefined;

  if (subject === 'it') {
    if (!fundCouponSavingsDisplay) return tq`Put it toward a honeymoon or getaway, on rates only for Joy couples.`;
    return withPhraseValues(
      fundCouponPercentOff
        ? tq`Use it for a honeymoon or getaway. Save up to an extra ${SAVINGS_SLOT} (${PERCENT_SLOT}) on rates only for Joy couples.`
        : tq`Use it for a honeymoon or getaway. Save up to an extra ${SAVINGS_SLOT} on rates only for Joy couples.`,
      {
        SAVINGS: <EmphasisedValue tone="discount">{fundCouponSavingsDisplay}</EmphasisedValue>,
        ...(fundCouponPercentOff && { PERCENT: <EmphasisedValue tone="discount">{`${fundCouponPercentOff}%`}</EmphasisedValue> }),
      },
    );
  }

  const balance = <EmphasisedValue tone="balance">{fundBalanceDisplay}</EmphasisedValue>;
  if (!fundCouponSavingsDisplay) {
    return withPhraseValues(tq`Put your ${BALANCE_SLOT} toward a honeymoon or getaway, on rates only for Joy couples.`, { BALANCE: balance });
  }

  // The rate is undefined on a fixed-amount code and once the cap bites, so it is dropped then.
  return withPhraseValues(
    fundCouponPercentOff
      ? tq`Use your ${BALANCE_SLOT} for a honeymoon or getaway. Save up to an extra ${SAVINGS_SLOT} (${PERCENT_SLOT}) on rates only for Joy couples.`
      : tq`Use your ${BALANCE_SLOT} for a honeymoon or getaway. Save up to an extra ${SAVINGS_SLOT} on rates only for Joy couples.`,
    {
      BALANCE: balance,
      SAVINGS: <EmphasisedValue tone="discount">{fundCouponSavingsDisplay}</EmphasisedValue>,
      ...(fundCouponPercentOff && { PERCENT: <EmphasisedValue tone="discount">{`${fundCouponPercentOff}%`}</EmphasisedValue> }),
    },
  );
};
